import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import api, { API, getToken, getUser } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Folder,
  FileMusic,
  FileVideo,
  ChevronLeft,
  Info,
  Download,
  Package,
  Loader2,
  Trash2,
  FolderInput,
  Pencil,
  Play,
  Search,
  X,
  Hash,
} from "lucide-react";
import { toast } from "sonner";
import { RaceBadge, RaceNumberDialog } from "../components/RaceNumberDialog";

export default function FileBrowser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = getUser();
  const isAdmin = user?.role === "admin";
  const [comp, setComp] = useState(null);
  const [path, setPath] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [infoFile, setInfoFile] = useState(null);
  const [infoData, setInfoData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveForm, setMoveForm] = useState({ disciplina: "", categoria: "", classe: "", tipologia: "" });
  const [moveBusy, setMoveBusy] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [opts, setOpts] = useState({ musica: [], video: [], categorie: [], classi: [], tipologie: [] });
  const [playTarget, setPlayTarget] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = no search yet, [] = empty results
  const [searching, setSearching] = useState(false);
  const [raceTarget, setRaceTarget] = useState(null);

  const onRaceSaved = (rn) => {
    const sid = raceTarget.submission_id;
    const upd = (arr) => arr.map((x) => (x.submission_id === sid ? { ...x, race_number: rn } : x));
    setItems(upd);
    setSearchResults((s) => (s ? upd(s) : s));
    setRaceTarget(null);
  };

  useEffect(() => {
    api.get(`/competitions/${id}`).then((r) => setComp(r.data));
  }, [id]);

  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([
      api.get("/meta/disciplines/musica"),
      api.get("/meta/disciplines/video"),
      api.get("/meta/categorie"),
      api.get("/meta/classes"),
      api.get("/meta/tipologie"),
    ])
      .then(([m, v, c, cl, t]) => setOpts({
        musica: m.data, video: v.data, categorie: c.data, classi: cl.data, tipologie: t.data,
      }))
      .catch(() => {});
  }, [isAdmin]);

  const reloadFolder = () =>
    api
      .get(`/competitions/${id}/browse`, { params: { path } })
      .then((r) => setItems(r.data.items));

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api
      .get(`/competitions/${id}/browse`, { params: { path } })
      .then((r) => setItems(r.data.items))
      .catch((err) => toast.error(err.response?.data?.detail || "Errore caricamento"))
      .finally(() => setLoading(false));
  }, [id, path]);

  const enterFolder = (item) => setPath(item.relative_path);
  const goUp = () => {
    if (!path) return;
    const parts = path.split("/").filter(Boolean);
    parts.pop();
    setPath(parts.join("/"));
  };

  const downloadFile = (item) => {
    const a = document.createElement("a");
    a.href = `${API}/competitions/${id}/download-file?path=${encodeURIComponent(
      item.relative_path
    )}&token=${encodeURIComponent(getToken())}`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadZip = (relativePath = "") => {
    const url = `${API}/competitions/${id}/download-zip?path=${encodeURIComponent(
      relativePath
    )}&token=${encodeURIComponent(getToken())}`;
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Download avviato: lo trovi nei download del browser");
  };

  const openInfo = async (item) => {
    setInfoFile(item);
    setInfoData(null);
    try {
      const r = await api.get(`/competitions/${id}/file-info`, {
        params: { path: item.relative_path },
      });
      setInfoData(r.data);
    } catch {
      setInfoData({ error: "Info non trovate per questo file" });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/submissions/${deleteTarget.submission_id}`);
      toast.success("File eliminato");
      setDeleteTarget(null);
      await reloadFolder();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  const openMove = async (item) => {
    try {
      const r = await api.get(`/competitions/${id}/file-info`, {
        params: { path: item.relative_path },
      });
      setMoveForm({
        disciplina: r.data.disciplina || "",
        categoria: r.data.categoria || "",
        classe: r.data.classe || "",
        tipologia: r.data.tipologia || "",
      });
      setMoveTarget({ ...item, info: r.data });
    } catch {
      toast.error("Impossibile aprire sposta");
    }
  };

  const submitMove = async () => {
    if (!moveTarget) return;
    const { disciplina, categoria, classe, tipologia } = moveForm;
    if (!disciplina || !categoria || !classe || !tipologia) {
      toast.error("Compila tutti i campi");
      return;
    }
    setMoveBusy(true);
    try {
      await api.patch(`/submissions/${moveTarget.submission_id}/move`, moveForm);
      toast.success("File spostato");
      setMoveTarget(null);
      await reloadFolder();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore spostamento");
    } finally {
      setMoveBusy(false);
    }
  };

  const openRename = (item) => {
    const dot = item.name.lastIndexOf(".");
    setRenameValue(dot > 0 ? item.name.slice(0, dot) : item.name);
    setRenameTarget(item);
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    if (!renameValue.trim()) {
      toast.error("Nome richiesto");
      return;
    }
    setRenameBusy(true);
    try {
      await api.patch(`/submissions/${renameTarget.submission_id}/rename`, {
        new_name: renameValue.trim(),
      });
      toast.success("File rinominato");
      setRenameTarget(null);
      await reloadFolder();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore rinomina");
    } finally {
      setRenameBusy(false);
    }
  };

  const streamUrl = (item) => {
    const token = getToken();
    return `${API}/competitions/${id}/stream-file?path=${encodeURIComponent(
      item.relative_path
    )}&token=${encodeURIComponent(token)}`;
  };

  const runSearch = async () => {
    const q = searchQ.trim();
    if (!q) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const r = await api.get(`/competitions/${id}/search`, { params: { q } });
      setSearchResults(r.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore ricerca");
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQ("");
    setSearchResults(null);
  };

  const isMaster = !path;
  const crumbs = path.split("/").filter(Boolean);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <button
          onClick={() => navigate("/admin")}
          data-testid="back-to-admin"
          className="text-white/60 hover:text-white text-sm flex items-center gap-1 mb-5"
        >
          <ChevronLeft className="w-4 h-4" /> {isAdmin ? "Torna ad Admin" : "Torna alle competizioni"}
        </button>

        <div className="flex flex-wrap justify-between items-end gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/40">File browser</div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight">
              {comp?.name || "..."}
            </h1>
            <div className="text-white/40 text-sm mt-1">
              <span className="cursor-pointer hover:text-white" onClick={() => setPath("")}>
                MASTER
              </span>
              {crumbs.map((c, i) => (
                <span key={i}>
                  {" / "}
                  <span
                    className="cursor-pointer hover:text-white"
                    onClick={() => setPath(crumbs.slice(0, i + 1).join("/"))}
                  >
                    {c}
                  </span>
                </span>
              ))}
            </div>
          </div>

          {isMaster && (
            <Button
              data-testid="download-competition-zip-btn"
              onClick={() => downloadZip("")}
              className="rounded-full bg-gradient-to-r from-[#FF5722] to-[#00E5FF] border-0 h-12 px-6 font-bold"
            >
              <Package className="w-5 h-5 mr-2" /> Download Competizione
            </Button>
          )}
          {!isMaster && (
            <Button
              data-testid="download-folder-zip-btn"
              onClick={() => downloadZip(path)}
              variant="outline"
              className="rounded-full border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
            >
              <Package className="w-4 h-4 mr-2" /> Download cartella (ZIP)
            </Button>
          )}
        </div>

        {path && (
          <Button
            variant="ghost"
            onClick={goUp}
            data-testid="folder-up-btn"
            className="text-white/60 hover:text-white mb-2"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Indietro
          </Button>
        )}

        {/* Search bar */}
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input
              data-testid="search-input"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Cerca in tutta la competizione (n° gara, cognome, nome, disciplina...)"
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
          </div>
          <Button
            data-testid="search-submit"
            onClick={runSearch}
            disabled={searching || !searchQ.trim()}
            className="rounded-full bg-gradient-to-r from-[#FF5722] to-[#00E5FF] text-white font-bold px-6"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
            CERCA
          </Button>
          {searchResults !== null && (
            <Button
              data-testid="search-clear"
              onClick={clearSearch}
              variant="ghost"
              className="text-white/60 hover:text-white"
            >
              <X className="w-4 h-4 mr-1" /> Chiudi ricerca
            </Button>
          )}
        </div>

        {searchResults !== null && (
          <div
            data-testid="search-results"
            className="mb-6 bg-[#0E0E11] border border-cyan-500/30 rounded-2xl overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-white/5 text-xs uppercase tracking-widest text-cyan-300">
              Risultati ricerca — {searchResults.length} file
            </div>
            {searchResults.length === 0 ? (
              <div className="p-8 text-center text-white/50">Nessun risultato</div>
            ) : (
              <ul className="divide-y divide-white/5">
                {searchResults.map((r) => (
                  <li
                    key={r.submission_id}
                    data-testid={`search-row-${r.submission_id}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-white/5"
                  >
                    {r.kind === "video" ? (
                      <FileVideo className="w-5 h-5 text-cyan-400" />
                    ) : (
                      <FileMusic className="w-5 h-5 text-violet-300" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-2 min-w-0">
                        <RaceBadge value={r.race_number} testId={`search-race-badge-${r.submission_id}`} />
                        <span className="truncate">{r.name}</span>
                      </div>
                      <div className="text-xs text-white/50 truncate">
                        {r.cognome} {r.nome} {r.gruppo ? `· ${r.gruppo}` : ""} ·{" "}
                        {r.disciplina} / {r.categoria} / {r.classe} / {r.tipologia}
                      </div>
                      <div className="text-[11px] text-white/30 truncate">{r.relative_path}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRaceTarget(r)}
                        data-testid={`search-race-btn-${r.submission_id}`}
                        className="hover:bg-orange-500/20 text-orange-300"
                        title="N° Gara"
                      >
                        <Hash className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPlayTarget({ name: r.name, relative_path: r.relative_path, kind: r.kind })}
                        data-testid={`search-play-${r.submission_id}`}
                        className="hover:bg-emerald-500/20 text-emerald-300"
                        title="Riproduci"
                      >
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openInfo({ relative_path: r.relative_path })}
                        className="hover:bg-cyan-500/20 text-cyan-300"
                        title="Info"
                      >
                        <Info className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadFile({ relative_path: r.relative_path, name: r.name })}
                        data-testid={`search-dl-${r.submission_id}`}
                        className="hover:bg-white/10 text-white/80"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="bg-[#0E0E11] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-white/50 flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Caricamento...
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-white/50">Cartella vuota</div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((it) => {
                const isMusic = it.name.toLowerCase().endsWith(".mp3");
                const isVideo = it.name.toLowerCase().endsWith(".mp4");
                const Icon = it.is_dir ? Folder : isVideo ? FileVideo : isMusic ? FileMusic : FileMusic;
                return (
                  <li
                    key={it.relative_path}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-white/5"
                    data-testid={`file-row-${it.name}`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        it.is_dir ? "text-orange-400" : isVideo ? "text-cyan-400" : "text-violet-300"
                      }`}
                    />
                    {it.is_dir ? (
                      <button
                        onClick={() => enterFolder(it)}
                        className="flex-1 text-left font-medium hover:text-cyan-300 flex items-center gap-2"
                      >
                        <span>{it.name}</span>
                        {it.file_count > 0 && (
                          <span
                            data-testid={`folder-count-${it.name}`}
                            className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-2 rounded-full text-xs font-bold bg-gradient-to-r from-[#FF5722] to-[#00E5FF] text-white shadow-[0_0_12px_rgba(0,229,255,0.35)]"
                            title={`${it.file_count} file`}
                          >
                            {it.file_count}
                          </span>
                        )}
                      </button>
                    ) : (
                      <div className="flex-1 min-w-0">
                        <div className="font-medium flex items-center gap-2 min-w-0">
                          <RaceBadge value={it.race_number} testId={`race-badge-${it.name}`} />
                          <span className="truncate">{it.name}</span>
                        </div>
                        {it.size != null && (
                          <div className="text-xs text-white/40">
                            {(it.size / (1024 * 1024)).toFixed(2)} MB
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-1">
                      {!it.is_dir && (
                        <>
                          {it.submission_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setRaceTarget(it)}
                              data-testid={`race-btn-${it.name}`}
                              className="hover:bg-orange-500/20 text-orange-300"
                              title="N° Gara"
                            >
                              <Hash className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setPlayTarget(it)}
                            data-testid={`play-btn-${it.name}`}
                            className="hover:bg-emerald-500/20 text-emerald-300"
                            title="Riproduci"
                          >
                            <Play className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openInfo(it)}
                            data-testid={`info-btn-${it.name}`}
                            className="hover:bg-cyan-500/20 text-cyan-300"
                            title="Info"
                          >
                            <Info className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadFile(it)}
                            data-testid={`download-btn-${it.name}`}
                            className="hover:bg-white/10 text-white/80"
                            title="Download"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {isAdmin && it.submission_id && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openRename(it)}
                                data-testid={`rename-btn-${it.name}`}
                                className="hover:bg-amber-500/20 text-amber-300"
                                title="Rinomina"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openMove(it)}
                                data-testid={`move-btn-${it.name}`}
                                className="hover:bg-violet-500/20 text-violet-300"
                                title="Sposta"
                              >
                                <FolderInput className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeleteTarget(it)}
                                data-testid={`delete-btn-${it.name}`}
                                className="hover:bg-red-500/20 text-red-300"
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </>
                      )}
                      {it.is_dir && it.file_count > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => downloadZip(it.relative_path)}
                          data-testid={`zip-btn-${it.name}`}
                          className="hover:bg-white/10 text-white/70"
                          title="Scarica ZIP"
                        >
                          <Package className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </main>

      <Dialog open={!!infoFile} onOpenChange={(o) => !o && setInfoFile(null)}>
        <DialogContent data-testid="info-dialog" className="bg-[#0E0E11] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Info inviante</DialogTitle>
          </DialogHeader>
          {!infoData && (
            <div className="text-white/60 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Caricamento...
            </div>
          )}
          {infoData?.error && <div className="text-red-300 text-sm">{infoData.error}</div>}
          {infoData && !infoData.error && (
            <div className="text-sm space-y-2">
              <InfoRow label="N° Gara" value={infoData.race_number != null ? `#${infoData.race_number}` : null} />
              <InfoRow label="Cognome" value={infoData.cognome} />
              <InfoRow label="Nome" value={infoData.nome} />
              {infoData.gruppo && <InfoRow label="Gruppo" value={infoData.gruppo} />}
              <InfoRow
                label="Disciplina"
                value={
                  infoData.disciplina ||
                  `${infoData.macro_discipline || ""} / ${infoData.sub_discipline || ""}`
                }
              />
              <InfoRow label="Categoria" value={infoData.categoria || infoData.eta} />
              <InfoRow label="Classe" value={infoData.classe} />
              <InfoRow label="Tipologia" value={infoData.tipologia} />
              <InfoRow label="Telefono" value={infoData.telefono} />
              <InfoRow label="ASA" value={infoData.asa} />
              <InfoRow label="Tecnico" value={infoData.tecnico} />
              <InfoRow label="Email" value={infoData.sender_email} />
              <InfoRow label="Originale" value={infoData.original_filename} />
              <InfoRow label="Caricato" value={infoData.created_at?.slice(0, 19).replace("T", " ")} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <RaceNumberDialog target={raceTarget} onClose={() => setRaceTarget(null)} onSaved={onRaceSaved} />

      {/* Player modal (audio/video) */}
      <Dialog open={!!playTarget} onOpenChange={(o) => !o && setPlayTarget(null)}>
        <DialogContent
          data-testid="player-dialog"
          className="bg-[#0E0E11] border-white/10 text-white max-w-2xl"
        >
          <DialogHeader>
            <DialogTitle className="truncate">
              {playTarget?.name || "Riproduzione"}
            </DialogTitle>
          </DialogHeader>
          {playTarget && (
            <div className="mt-2">
              {playTarget.name?.toLowerCase().endsWith(".mp4") ||
              playTarget.kind === "video" ? (
                <video
                  key={playTarget.relative_path}
                  data-testid="player-video"
                  src={streamUrl(playTarget)}
                  controls
                  autoPlay
                  className="w-full max-h-[70vh] rounded-lg bg-black"
                />
              ) : (
                <audio
                  key={playTarget.relative_path}
                  data-testid="player-audio"
                  src={streamUrl(playTarget)}
                  controls
                  autoPlay
                  className="w-full"
                />
              )}
              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  onClick={() => downloadFile(playTarget)}
                  data-testid="player-download"
                  className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10"
                >
                  <Download className="w-4 h-4 mr-2" /> Scarica
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent
          data-testid="delete-dialog"
          className="bg-[#0E0E11] border-white/10 text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo file?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              Il file <span className="text-white font-semibold">{deleteTarget?.name}</span> non
              sarà più visibile e verrà escluso dai download ZIP. L'azione è definitiva.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel"
              className="bg-white/5 border-white/10 text-white hover:bg-white/10"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move dialog */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => !o && setMoveTarget(null)}>
        <DialogContent
          data-testid="move-dialog"
          className="bg-[#0E0E11] border-white/10 text-white max-w-md"
        >
          <DialogHeader>
            <DialogTitle>Sposta file</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-white/50 -mt-2 break-all">
            {moveTarget?.name}
          </div>
          <div className="space-y-3 mt-2">
            <MoveSelect
              label="Disciplina"
              testId="move-disciplina"
              value={moveForm.disciplina}
              onChange={(v) => setMoveForm((f) => ({ ...f, disciplina: v }))}
              options={moveTarget?.kind === "video" ? opts.video : opts.musica}
            />
            <MoveSelect
              label="Categoria"
              testId="move-categoria"
              value={moveForm.categoria}
              onChange={(v) => setMoveForm((f) => ({ ...f, categoria: v }))}
              options={opts.categorie}
            />
            <MoveSelect
              label="Classe"
              testId="move-classe"
              value={moveForm.classe}
              onChange={(v) => setMoveForm((f) => ({ ...f, classe: v }))}
              options={opts.classi}
            />
            <MoveSelect
              label="Tipologia"
              testId="move-tipologia"
              value={moveForm.tipologia}
              onChange={(v) => setMoveForm((f) => ({ ...f, tipologia: v }))}
              options={opts.tipologie}
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setMoveTarget(null)}
              className="text-white/70 hover:bg-white/10"
              data-testid="move-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={submitMove}
              disabled={moveBusy}
              data-testid="move-submit"
              className="bg-gradient-to-r from-[#FF5722] to-[#00E5FF] text-white font-bold"
            >
              {moveBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Sposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent
          data-testid="rename-dialog"
          className="bg-[#0E0E11] border-white/10 text-white max-w-md"
        >
          <DialogHeader>
            <DialogTitle>Rinomina file</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            <div className="text-xs text-white/50 break-all">
              Attuale: {renameTarget?.name}
            </div>
            <div className="flex items-center gap-2">
              <Input
                data-testid="rename-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Nuovo nome (senza estensione)"
                className="bg-white/5 border-white/10 text-white"
              />
              <span className="text-white/50 text-sm">
                {renameTarget?.name
                  ? renameTarget.name.slice(renameTarget.name.lastIndexOf("."))
                  : ""}
              </span>
            </div>
            <div className="text-[11px] text-white/40">
              Caratteri speciali verranno automaticamente sostituiti con "-".
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setRenameTarget(null)}
              className="text-white/70 hover:bg-white/10"
              data-testid="rename-cancel"
            >
              Annulla
            </Button>
            <Button
              onClick={submitRename}
              disabled={renameBusy}
              data-testid="rename-submit"
              className="bg-gradient-to-r from-[#FF5722] to-[#00E5FF] text-white font-bold"
            >
              {renameBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Rinomina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MoveSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-white/50 mb-1">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="bg-white/5 border-white/10 text-white">
          <SelectValue placeholder={`Seleziona ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent className="bg-[#0E0E11] border-white/10 text-white max-h-72">
          {(options || []).map((o) => (
            <SelectItem key={o} value={o} className="text-white hover:bg-white/10">
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-white/5">
      <div className="text-white/50 text-xs uppercase tracking-wider">{label}</div>
      <div className="text-right break-all">{value || "-"}</div>
    </div>
  );
}
